import { datadogRum } from '@datadog/browser-rum';
import {
	type User,
	useAuthInfo,
	useLogoutFunction,
	useRedirectFunctions,
} from '@propelauth/react';
import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';

type RedirectToLoginPage = ReturnType<
	typeof useRedirectFunctions
>['redirectToLoginPage'];
type PropelLogout = ReturnType<typeof useLogoutFunction>;

export type PropelAuthBridgeValue = {
	loading: boolean;
	user: User | null | undefined;
	isLoggedIn: boolean;
	accessToken: string | null;
	redirectToLoginPage: RedirectToLoginPage;
	logout: PropelLogout;
};

// Reaching either of these means the app asked PropelAuth to do something while
// the PropelAuth provider is not mounted — always a programming error. After the
// reactive mount in AuthProvider this is unreachable in normal operation (the
// provider is mounted whenever PropelAuth is the active/undecided provider), so
// these exist purely to make a future wiring regression observable instead of a
// silent no-op (a mystery spinner). We report rather than throw because these
// sit on the user-facing login/logout path and a throw would white-screen.
function reportBridgeMisuse(method: string) {
	const error = new Error(
		`PropelAuthBridge default ${method} called with no PropelAuth provider mounted`,
	);
	// eslint-disable-next-line no-console
	console.error(error);
	datadogRum.addError(error);
}

export const defaultPropelAuthBridgeValue: PropelAuthBridgeValue = {
	loading: false,
	user: null,
	isLoggedIn: false,
	accessToken: null,
	redirectToLoginPage: () => reportBridgeMisuse('redirectToLoginPage'),
	logout: async () => reportBridgeMisuse('logout'),
};

export const PropelAuthBridgeContext = createContext<PropelAuthBridgeValue>(
	defaultPropelAuthBridgeValue,
);

export const usePropelAuthBridge = () => useContext(PropelAuthBridgeContext);

/**
 * Bridges the PropelAuth SDK hooks into a context we own, so `useAuth` can read
 * PropelAuth state whether or not the SDK provider is mounted. This is what lets
 * AuthProvider mount PropelAuth conditionally (stopping its background
 * /refresh_token polling for Auth0/niche users).
 */
export function PropelAuthBridge({ children }: { children: React.ReactNode }) {
	const { loading, user, isLoggedIn, accessToken } = useAuthInfo();
	const { redirectToLoginPage } = useRedirectFunctions();
	const logout = useLogoutFunction();

	// The SDK returns a fresh `redirectToLoginPage`/`logout` reference on every
	// render (useClientRefCallback wraps them in useCallback with a dep that
	// itself changes each render). Route them through refs + stable wrappers so
	// the context value below can actually stay referentially stable.
	const redirectRef = useRef(redirectToLoginPage);
	redirectRef.current = redirectToLoginPage;
	const logoutRef = useRef(logout);
	logoutRef.current = logout;

	const stableRedirect = useCallback<RedirectToLoginPage>(
		(...args) => redirectRef.current(...args),
		[],
	);
	const stableLogout = useCallback<PropelLogout>(
		(...args) => logoutRef.current(...args),
		[],
	);

	const value = useMemo<PropelAuthBridgeValue>(
		() => ({
			loading,
			user,
			isLoggedIn: isLoggedIn || false,
			accessToken: accessToken ?? null,
			redirectToLoginPage: stableRedirect,
			logout: stableLogout,
		}),
		[loading, user, isLoggedIn, accessToken, stableRedirect, stableLogout],
	);

	return (
		<PropelAuthBridgeContext.Provider value={value}>
			{children}
		</PropelAuthBridgeContext.Provider>
	);
}
